import React, { useState, useCallback, useEffect } from 'react';
import { request } from '../api/request';
import { buildImageGenerationUrl } from '../utils/aiEndpoints';

interface AIImageGenerationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelect: (imageUrl: string) => void;
  defaultPrompt?: string;
}

interface ImageLoadState {
  url: string;
  loading: boolean;
  error: boolean;
  loaded: boolean;
}

export const AIImageGenerationDrawer: React.FC<AIImageGenerationDrawerProps> = ({
  isOpen,
  onClose,
  onImageSelect,
  defaultPrompt = ''
}) => {
  // Local state instead of global Redux state
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [imageLoadStates, setImageLoadStates] = useState<ImageLoadState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState(defaultPrompt || '');

  // Update local prompt when drawer opens with default prompt
  useEffect(() => {
    if (isOpen && defaultPrompt) {
      setPrompt(defaultPrompt);
    }
  }, [isOpen, defaultPrompt]);

  // Clear state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedImages([]);
      setImageLoadStates([]);
      setError(null);
      setPrompt(defaultPrompt || '');
    }
  }, [isOpen, defaultPrompt]);

  // Initialize image load states when images change
  useEffect(() => {
    if (generatedImages.length > 0 && !loading) {
      const initialStates = generatedImages.map(url => ({
        url,
        loading: false,
        error: false,
        loaded: true
      }));
      setImageLoadStates(initialStates);
    }
  }, [generatedImages, loading]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);
    
    // Show skeleton placeholders immediately
    const skeletonPlaceholders = Array(4).fill('').map((_, index) => `skeleton-${index}`);
    setGeneratedImages(skeletonPlaceholders);

    try {
      const { url, base } = buildImageGenerationUrl(prompt);
      const response = await request({
        method: 'GET',
        url,
        base,
        requiresAuth: false,
        requiresRestaurantId: false,
        timeout:120000
      });

      // Handle different response formats
      let images: string[] = [];
      
      if (Array.isArray(response?.data)) {
        // Direct array response
        images = response.data;
      } else if (Array.isArray((response?.data as any)?.images)) {
        // Nested images property
        images = (response.data as any).images;
      } else if (Array.isArray(response)) {
        // Response is directly an array
        images = response;
      }

      if (!images || !Array.isArray(images) || images.length === 0) {
        setError('No images generated. Please try again.');
        setGeneratedImages([]);
        return;
      }

      setGeneratedImages(images);
      console.log('Generated images received:', images);
    } catch (err: any) {
      let errorMessage = 'Failed to generate images';
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      console.error('Image generation error:', err);
      setError(errorMessage);
      setGeneratedImages([]);
    } finally {
      setLoading(false);
    }
  }, [prompt]);


  const handleImageSelect = useCallback((imageUrl: string) => {
    onImageSelect(imageUrl);
    onClose();
  }, [onImageSelect, onClose]);

  const handleClose = useCallback(() => {
    setGeneratedImages([]);
    setError(null);
    setPrompt(defaultPrompt || '');
    onClose();
  }, [onClose, defaultPrompt]);


  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 transition-opacity duration-300 bg-black/30"
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div 
        className={`fixed top-0 right-0 h-full w-96 max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-semibold text-gray-900">Generate Images with AI</h2>
            <button 
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Prompt Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the image you want to generate
              </label>
              <div className="space-y-3">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Pizza House restaurant banner, delicious food background"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  disabled={loading}
                  autoFocus
                />
                <button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="w-full px-4 py-3 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
                >
                  {loading ? 'Generating...' : 'Generate Images'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Be specific about what you want - include style, colors, and context for better results
              </p>
            </div>


            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-600 font-medium text-sm">Generation Failed</p>
                </div>
                <p className="text-red-600 mt-1 text-sm">{error}</p>
                <button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                  className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Generated Images Grid */}
            {generatedImages && generatedImages.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  {loading ? 'Generating Images...' : `Generated Images (${generatedImages.length})`}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {generatedImages.map((imageUrl, index) => {
                    const loadState = imageLoadStates[index];
                    const isSkeletonPlaceholder = imageUrl.startsWith('skeleton-');
                    
                    return (
                      <div
                        key={`${imageUrl}-${index}`}
                        className={`relative rounded-lg overflow-hidden border-2 transition-colors duration-200 bg-gray-50 ${
                          isSkeletonPlaceholder || loading 
                            ? 'border-gray-200' 
                            : 'cursor-pointer group border-gray-200 hover:border-red-500'
                        }`}
                        onClick={() => !isSkeletonPlaceholder && !loading && handleImageSelect(imageUrl)}
                        style={{ minHeight: '160px' }}
                      >
                        {/* Skeleton Loading State */}
                        {(isSkeletonPlaceholder || loading) && (
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <div className="w-16 h-2 bg-gray-300 rounded mx-auto mb-1"></div>
                                <div className="w-12 h-2 bg-gray-300 rounded mx-auto"></div>
                              </div>
                            </div>
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                          </div>
                        )}

                        {/* Actual Image */}
                        {!isSkeletonPlaceholder && !loading && (
                          <img
                            src={imageUrl}
                            alt={`Generated image ${index + 1}`}
                            className="w-full h-40 object-cover"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                          />
                        )}

                        {/* Select checkmark icon - only visible on hover */}
                        {!isSkeletonPlaceholder && !loading && (
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-red-500 text-white rounded-full p-1 shadow-lg">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}

                        {/* Image URL Debug Info (visible on hover) */}
                        {!isSkeletonPlaceholder && !loading && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="truncate">{imageUrl}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && (!generatedImages || generatedImages.length === 0) && (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-base font-medium text-gray-900 mb-2">Ready to Generate</h3>
                <p className="text-gray-500 mb-3 text-sm">Enter a description above and click Generate to create AI images</p>
                <div className="text-xs text-gray-400">
                  <p className="font-medium">💡 Tips for better results:</p>
                  <ul className="mt-2 space-y-1 text-left">
                    <li>• Be specific about style and colors</li>
                    <li>• Include context (restaurant, food, banner, etc.)</li>
                    <li>• Mention mood or atmosphere you want</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

AIImageGenerationDrawer.displayName = 'AIImageGenerationDrawer';
